/**
 * Cross-workshop faceted search (CC-SEARCH). Searches every workshop's
 * records at once, groups the hits by workshop, shows per-category facet
 * counts, and lets a TagFilter narrow by tag. Clicking a hit routes to its
 * home workshop and opens the right modal — with the rAF deferral the
 * master plan flags (Risk #7): when the hit lives in a *different* workshop
 * the route must mount before the target modal opens, so `open()` is wrapped
 * in `requestAnimationFrame`.
 *
 * Launched from the workshop HUD; spans the whole program. Reads live store
 * snapshots (hydrated on open via `hydrateAllRegisters`). Mock-mode only —
 * no BFF, no key.
 */

import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Search, X } from "lucide-react";
import { Dialog, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { PanelDialogContent } from "@/components/shell/PanelDialogContent";
import { WORKSHOPS } from "@/content/workshops";
import { FIELD_CLASS } from "@/features/workshop/modals/log-controls.utils";
import { TagFilter } from "@/components/tags/TagFilter";
import { collectTags } from "@/lib/tags";
import { stripNs, TAG_NS_OPPORTUNITY } from "@/lib/taxonomy";
import {
  searchAllWorkshops,
  type SearchCategory,
  type SearchHit,
  type SearchSources,
} from "@/lib/search";
import { useWorkshopModals, type OpenModal } from "@/features/workshop/modals/useWorkshopModals";
import { useJumpTarget } from "@/features/search/useJumpTarget";
import { hydrateAllRegisters } from "@/features/missionControl/useProgramRollup";
import { useDecisions } from "@/stores/useDecisions";
import { useActions } from "@/stores/useActions";
import { useAssumptions } from "@/stores/useAssumptions";
import { useTranscripts } from "@/stores/useTranscripts";
import { useCaptures } from "@/stores/useCaptures";
import { useAssetLocker } from "@/stores/useAssetLocker";
import { useFindings } from "@/stores/useFindings";
import { useOpportunities } from "@/stores/useOpportunities";
import { useRecommendations } from "@/stores/useRecommendations";
import { useFacilitationNotes } from "@/stores/useFacilitationNotes";

type CrossSearchModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The workshop the search was launched from (drives "same workshop?"). */
  workshopId: string;
};

/** Which workshop modal a hit category opens. The Workshop Log is the
 *  catch-all editing surface for the registers. Uploads live on the
 *  dashboard Asset Locker (not a modal) — return null and let the jump
 *  target scroll/open the chip there. */
function modalForCategory(category: SearchCategory): Exclude<OpenModal, null> | null {
  if (category === "capture") return "captures";
  if (category === "transcript") return "transcript";
  if (category === "upload") return null;
  return "decision-log";
}

const CATEGORY_LABEL: Record<SearchCategory, string> = {
  capture: "captures",
  decision: "decisions",
  action: "actions",
  assumption: "assumptions",
  transcript: "transcripts",
  upload: "uploads",
  finding: "findings",
  opportunity: "opportunities",
  recommendation: "recommendations",
  note: "notes",
};

export function CrossSearchModal({ open, onOpenChange, workshopId }: CrossSearchModalProps) {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const openModal = useWorkshopModals((s) => s.open);
  const setJumpTarget = useJumpTarget((s) => s.set);

  const [query, setQuery] = useState("");
  const [activeTags, setActiveTags] = useState<string[]>([]);

  // Whole-map subscriptions for every register.
  const decisions = useDecisions((s) => s.entriesByWorkshop);
  const actions = useActions((s) => s.entriesByWorkshop);
  const assumptions = useAssumptions((s) => s.entriesByWorkshop);
  const transcripts = useTranscripts((s) => s.entriesByWorkshop);
  const captures = useCaptures((s) => s.entriesByWorkshop);
  const uploads = useAssetLocker((s) => s.entriesByWorkshop);
  const findings = useFindings((s) => s.entriesByWorkshop);
  const opportunities = useOpportunities((s) => s.entriesByWorkshop);
  const recommendations = useRecommendations((s) => s.entriesByWorkshop);
  const notes = useFacilitationNotes((s) => s.entriesByWorkshop);

  useEffect(() => {
    if (open) void hydrateAllRegisters();
  }, [open]);

  const sourcesByWorkshop = useMemo(
    () =>
      WORKSHOPS.map((w) => ({
        workshopId: w.id,
        sources: {
          captures: captures[w.id] ?? [],
          decisions: decisions[w.id] ?? [],
          actions: actions[w.id] ?? [],
          assumptions: assumptions[w.id] ?? [],
          transcripts: transcripts[w.id] ?? [],
          uploads: uploads[w.id] ?? [],
          findings: findings[w.id] ?? [],
          opportunities: opportunities[w.id] ?? [],
          recommendations: recommendations[w.id] ?? [],
          notes: notes[w.id] ?? [],
        } satisfies SearchSources,
      })),
    [decisions, actions, assumptions, transcripts, captures, uploads, findings, opportunities, recommendations, notes],
  );

  // All tags across the program (free-form + de-namespaced opportunity tags).
  const allTags = useMemo(() => {
    const everything = sourcesByWorkshop.flatMap((g) => [
      ...g.sources.captures,
      ...g.sources.decisions,
      ...g.sources.actions,
      ...g.sources.assumptions,
      ...g.sources.transcripts,
      ...g.sources.findings,
      ...g.sources.opportunities,
      ...g.sources.recommendations,
      ...g.sources.notes,
    ]);
    return collectTags(everything);
  }, [sourcesByWorkshop]);

  const results = useMemo(
    () => searchAllWorkshops(query, sourcesByWorkshop, { activeTags }),
    [query, sourcesByWorkshop, activeTags],
  );

  const hasCriteria = query.trim().length > 0 || activeTags.length > 0;

  function openHit(wsId: string, hit: SearchHit): void {
    setJumpTarget({ workshopId: wsId, category: hit.category, id: hit.id });
    const target = modalForCategory(hit.category);
    if (wsId !== workshopId) {
      navigate(`/workshop/${wsId}`, { state: { from: pathname + search } });
    }
    // Defer so the (possibly just-mounted) workshop route's ModalTriggers is
    // present before we set the active modal. open() replaces this modal.
    // Uploads: no modal — closing search + navigation is enough; AssetLocker
    // honours the jump target on the dashboard.
    if (target) {
      requestAnimationFrame(() => openModal(target));
    } else {
      onOpenChange(false);
    }
  }

  const facetEntries = (Object.keys(results.facets) as SearchCategory[])
    .filter((c) => results.facets[c] > 0)
    .map((c) => ({ category: c, count: results.facets[c] }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <PanelDialogContent
        className="w-[90vw] max-w-[820px] border-0 bg-transparent p-0 shadow-none"
        style={{ outline: "none" }}
      >
        <DialogTitle className="sr-only">Cross-workshop search</DialogTitle>
        <DialogDescription className="sr-only">
          Search records across every workshop, filter by tag, and jump to a hit.
        </DialogDescription>
        <div className="modal-shell ig-cross" style={{ width: "100%", maxHeight: "85vh" }}>
          <div className="modal-header">
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Search aria-hidden style={{ width: 16, height: 16, color: "var(--accent-true-blue)" }} />
              <span className="modal-title">CROSS-WORKSHOP SEARCH</span>
              {results.total > 0 && (
                <span
                  style={{
                    padding: "1px 8px",
                    background: "rgba(var(--accent-sky-rgb), 0.08)",
                    border: "1px solid rgba(var(--accent-sky-rgb), 0.25)",
                    borderRadius: 10,
                    fontSize: "var(--fs-micro)",
                    color: "var(--accent-sky)",
                    fontWeight: 600,
                    letterSpacing: 1,
                  }}
                >
                  {results.total} hit{results.total === 1 ? "" : "s"} · {results.byWorkshop.length} workshop
                  {results.byWorkshop.length === 1 ? "" : "s"}
                </span>
              )}
            </div>
            <button type="button" onClick={() => onOpenChange(false)} className="modal-close-btn" aria-label="Close">
              <X aria-hidden style={{ width: 12, height: 12 }} />
              CLOSE
            </button>
          </div>

          <div className="modal-body" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <input
              type="search"
              aria-label="Search across all workshops"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search decisions, findings, recommendations… across all 8 workshops"
              className={FIELD_CLASS}
            />
            {allTags.length > 0 && (
              <TagFilter
                allTags={allTags}
                selected={activeTags}
                onChange={setActiveTags}
                formatLabel={(t) => stripNs(TAG_NS_OPPORTUNITY, t)}
              />
            )}

            {facetEntries.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {facetEntries.map((f) => (
                  <span
                    key={f.category}
                    style={{
                      fontSize: "var(--fs-micro)",
                      color: "var(--text-secondary)",
                      background: "rgba(var(--accent-true-blue-rgb), 0.08)",
                      border: "1px solid rgba(var(--accent-true-blue-rgb), 0.18)",
                      borderRadius: 999,
                      padding: "1px 8px",
                      letterSpacing: 0.4,
                    }}
                  >
                    {CATEGORY_LABEL[f.category]} {f.count}
                  </span>
                ))}
              </div>
            )}

            {!hasCriteria && (
              <p style={{ fontSize: "var(--fs-label)", color: "var(--text-muted)" }}>
                Type a query or pick a tag to search every workshop at once.
              </p>
            )}
            {hasCriteria && results.total === 0 && (
              <p style={{ fontSize: "var(--fs-label)", color: "var(--text-muted)" }}>No matches across any workshop.</p>
            )}

            {results.byWorkshop.map((group) => {
              const ws = WORKSHOPS.find((w) => w.id === group.workshopId);
              return (
                <section key={group.workshopId}>
                  <div
                    style={{
                      fontSize: "var(--fs-label)",
                      letterSpacing: "1.5px",
                      textTransform: "uppercase",
                      color: "var(--accent-sky)",
                      fontWeight: 700,
                      marginBottom: 6,
                    }}
                  >
                    {ws?.longTitle ?? group.workshopId} ({group.hits.length})
                  </div>
                  <ul className="flex flex-col gap-1.5">
                    {group.hits.map((hit) => (
                      <li key={`${hit.category}-${hit.id}`}>
                        <button
                          type="button"
                          onClick={() => openHit(group.workshopId, hit)}
                          className="flex w-full items-center gap-2.5 rounded-lg border px-2.5 py-2 text-left cursor-pointer transition-colors border-[rgba(var(--accent-true-blue-rgb),0.12)] bg-[rgba(var(--accent-true-blue-rgb),0.04)] hover:bg-[rgba(var(--accent-true-blue-rgb),0.10)] hover:border-[rgba(var(--accent-true-blue-rgb),0.30)] focus-visible:outline-none focus-visible:border-[var(--accent-true-blue)]"
                        >
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div
                              style={{
                                fontSize: "var(--fs-base)",
                                fontWeight: 600,
                                color: "var(--text-primary)",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {hit.title}
                            </div>
                            <div style={{ fontSize: "var(--fs-micro)", color: "var(--text-muted)" }}>
                              {CATEGORY_LABEL[hit.category].replace(/s$/, "")}
                              {hit.subtitle ? ` · ${hit.subtitle}` : ""}
                            </div>
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
                </section>
              );
            })}
          </div>

          <div className="modal-footer">
            <span className="modal-footer-label">XAES Phase Zero</span>
            <span className="modal-footer-sep">|</span>
            <span className="modal-footer-ws">Cross-workshop search</span>
          </div>
        </div>
      </PanelDialogContent>
    </Dialog>
  );
}
